<script setup>
import { computed, useSlots } from 'vue';
import { CsSpinner } from '../spinner';
import { CsIcon } from '../icon';
import { buttonRecipe } from './button.recipe';

/**
 * @typedef {import('../icon/icon-types').IconName} IconName
 */

/**
 * @typedef {object} ButtonProps
 * @property {('default'|'primary'|'secondary'|'critical'|'success'|'contrast'|'tertiary'|'magic'|'subtle'|'caution')} [variant='default'] - Variant of the button
 * @property {('sm'|'default'|'lg')} `[size='default'] - Size of the button
 * @property {boolean} [isIconOnly=false] - Whether the button is only an icon
 * @property {boolean} [isDisabled=false] - Whether the button is disabled
 * @property {boolean} [isLoading=false] - Whether the button is in loading state
 * @property {IconName} [prefixIcon=''] - Name of the icon to display
 * @property {IconName} [suffixIcon=''] - Name of the icon to display
 */

/**
 * @type {ButtonProps}
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'default',
  },
  size: {
    type: String,
    default: 'default',
  },
  isIconOnly: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  prefixIcon: {
    type: String,
    default: '',
  },
  suffixIcon: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['click']);

const slots = useSlots();

function hasSlot(name) {
  return !!slots[name];
}

const buttonClasses = computed(() =>
  buttonRecipe({
    variant: props.variant,
    size: props.size,
    isIconOnly: props.isIconOnly,
    isDisabled: props.isDisabled,
    isLoading: props.isLoading,
  })
);
</script>

<template>
  <button
    data-component="CsButton"
    :class="buttonClasses.button()"
    :disabled="isDisabled || isLoading"
    v-bind="$attrs"
    @click="e => emit('click', e)"
  >
    <span
      v-if="hasSlot('prefix') || prefixIcon"
      data-slot="prefix"
      :class="buttonClasses.prefix()"
    >
      <CsIcon v-if="prefixIcon" :name="prefixIcon" class="mr-0.5 -ml-1" />
      <slot v-else name="prefix" />
    </span>

    <span data-slot="label" :class="buttonClasses.label()">
      <slot />
    </span>

    <span
      v-if="hasSlot('suffix') || suffixIcon"
      data-slot="suffix"
      :class="buttonClasses.prefix()"
    >
      <CsIcon v-if="suffixIcon" :name="suffixIcon" class="ml-0.5 -mr-1" />
      <slot v-else name="suffix" />
    </span>

    <span v-if="isLoading" data-slot="loading" :class="buttonClasses.loading()">
      <CsSpinner size="xs" />
    </span>
  </button>
</template>
